/* Casa de Alix · couche motion v2 (agent home)
   À charger en <script defer> AVANT casa.js (il reprend le carrousel hero à casa.js
   en retirant l'attribut data-slides avant que casa.js ne le lise).
   Dépendances optionnelles (globales UMD, chargées avant ce fichier) :
   EmblaCarousel, EmblaCarouselAutoplay, EmblaCarouselWheelGestures, Lenis.
   Sans elles, chaque module se désactive proprement et casa.js garde la main. */
(() => {
  const html = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const design = !!(window.Shopify && window.Shopify.designMode);
  const cfg = Object.assign({
    smooth: html.hasAttribute('data-casa-smooth'),   // Lenis, opt-in
    stagger: 60,                                      // ms entre deux cartes d'un même groupe
    autoplay: 4500,                                   // ms, carrousel hero
    heroStep: 80                                      // ms entre deux éléments du hero
  }, window.CASA_MOTION || {});
  // 2.7 : si le filet de sécurité du head a déjà tout montré (script en retard), on ne recache rien
  const off = !!window.CASA_MOTION_OFF;
  if (!off) html.classList.add('ca-motion');
  window.CASA_MOTION_READY = true;

  /* Quand aucune section n'a reçu de data-reveal (thème pas encore patché),
     on marque les blocs du thème actuel pour que la couche serve tout de suite. */
  const AUTO = [
    '.ca-shead', '.ca-card', '.ca-matter__a', '.ca-matter__text', '.ca-minis__intro',
    '.ca-atelier__portrait', '.ca-atelier__text', '.ca-promises li', '.ca-faq__grid > *',
    '.ca-story > .ca-wrap > *', '.ca-footer__grid > *'
  ];
  const AUTO_HERO = ['.ca-hero__text > .ca-label', '.ca-hero__text > .ca-display', '.ca-hero__text > .ca-lead', '.ca-hero__text > .ca-btn', '.ca-plp-head .ca-label', '.ca-plp-head .ca-display', '.ca-plp-head .ca-chead__meta', '.ca-plp-head .ca-chead__text', '.ca-plp-head .ca-search__form'];

  const autoTag = (scope) => {
    // 1.8 : on complète les sections qui n'ont pas leurs propres marques, sans écraser celles qui en ont
    const free = (el) => !el.closest('[data-reveal], [data-hero], .ca-is-in') && !el.querySelector('[data-reveal], [data-hero]');
    AUTO.forEach((s) => scope.querySelectorAll(s).forEach((el) => { if (!el.closest('.ca-hero') && free(el)) el.setAttribute('data-reveal', ''); }));
    AUTO_HERO.forEach((s) => scope.querySelectorAll(s).forEach((el) => { if (free(el)) el.setAttribute('data-hero', ''); }));
    scope.querySelectorAll('.ca-hero .ca-slides, .ca-hero__media').forEach((el) => el.setAttribute('data-hero', 'media'));
  };

  /* ---------------------------------------------------------------- */
  /* 1. Reveal au scroll : une fois, opacité + translate, stagger 50 ms  */
  /* ---------------------------------------------------------------- */
  const settle = (el, ms) => setTimeout(() => { el.removeAttribute('data-reveal'); el.style.removeProperty('--ca-d'); }, ms);
  const revealNow = (els) => els.forEach((el) => { el.classList.add('ca-is-in'); settle(el, 0); });
  let io = null;
  const reveal = (scope) => {
    const els = [...scope.querySelectorAll('[data-reveal]')].filter((el) => !el.classList.contains('ca-is-in'));
    if (!els.length) return;
    if (off || reduce.matches || design || !('IntersectionObserver' in window)) return revealNow(els);
    io = io || new IntersectionObserver((entries) => {
      const hits = entries.filter((e) => e.isIntersecting).map((e) => e.target)
        .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
      const perGroup = new Map();
      hits.forEach((el) => {
        const g = el.closest('[data-reveal-group]') || el.parentElement;
        const k = perGroup.get(g) || 0; perGroup.set(g, k + 1);
        const d = k * cfg.stagger;
        el.style.setProperty('--ca-d', d + 'ms');
        el.classList.add('ca-is-in');
        io.unobserve(el);
        settle(el, d + 1800);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
    els.forEach((el) => io.observe(el));
  };

  /* ---------------------------------------------------------------- */
  /* 2. Entrée du hero : label, titre, texte, bouton en cascade ; image  */
  /* ---------------------------------------------------------------- */
  const hero = (scope) => {
    const els = [...scope.querySelectorAll('[data-hero]')].filter((el) => !el.classList.contains('ca-is-in'));
    if (!els.length) return;
    if (off || reduce.matches || design) return els.forEach((el) => el.classList.add('ca-is-in'));
    let k = 0;
    const run = () => els.forEach((el) => {
      const media = el.dataset.hero === 'media';
      const d = media ? 0 : 40 + (k++) * cfg.heroStep;
      el.style.setProperty('--ca-d', d + 'ms');
      el.classList.add('ca-is-in');
      setTimeout(() => el.style.removeProperty('--ca-d'), d + 1400);
    });
    // On attend la police de titre (max 500 ms) pour que le script n'arrive pas en deux temps
    const fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    Promise.race([fonts, new Promise((r) => setTimeout(r, 500))]).then(() => requestAnimationFrame(run));
  };

  /* ---------------------------------------------------------------- */
  /* 3. Carrousel hero : Embla (drag, momentum, snap, boucle, autoplay)  */
  /* ---------------------------------------------------------------- */
  const sliders = (scope) => {
    if (!window.EmblaCarousel) return; // casa.js garde son slider natif
    scope.querySelectorAll('[data-slides]').forEach((root) => {
      const track = root.querySelector('[data-track]');
      if (!track || track.children.length < 2) return;
      root.removeAttribute('data-slides');            // casa.js ne le verra pas
      root.setAttribute('data-slides-embla', '');
      let vp = root.querySelector('.ca-slides__viewport');
      if (!vp) { vp = document.createElement('div'); vp.className = 'ca-slides__viewport'; track.replaceWith(vp); vp.appendChild(track); }
      root.classList.add('ca-embla');

      const jump = reduce.matches;
      const plugins = [];
      if (!jump && window.EmblaCarouselAutoplay) {
        plugins.push(window.EmblaCarouselAutoplay({
          delay: cfg.autoplay, playOnInit: true,
          stopOnInteraction: false,   // reprend après un drag ou une flèche
          stopOnMouseEnter: true,     // pause au survol, reprise à la sortie
          stopOnFocusIn: true,        // pause quand le clavier entre, reprise à la sortie
          rootNode: () => root
        }));
      }
      if (window.EmblaCarouselWheelGestures) plugins.push(window.EmblaCarouselWheelGestures());

      const embla = window.EmblaCarousel(vp, {
        loop: true, align: 'start', skipSnaps: false, duration: 26, dragThreshold: 8, containScroll: false,
        breakpoints: { '(max-width: 860px)': { align: 'center' } }
      }, plugins);
      root._embla = embla;

      const cur = root.querySelector('[data-current]');
      const sync = () => { if (cur) cur.textContent = String(embla.selectedScrollSnap() + 1).padStart(2, '0'); };
      embla.on('select', sync); sync();

      const autoplay = () => (embla.plugins() || {}).autoplay;
      let hover = false;
      root.addEventListener('mouseenter', () => { hover = true; });
      root.addEventListener('mouseleave', () => { hover = false; });
      // Flèches et clavier : on repart de zéro, sauf si la souris est encore sur le carrousel
      // (le plugin relance l'autoplay quand le focus quitte la piste, on l'en empêche)
      const nav = (dir) => { dir > 0 ? embla.scrollNext(jump) : embla.scrollPrev(jump); const a = autoplay(); if (a) { (hover || paused) ? a.stop() : a.reset(); } };
      let paused = false;
      const pauseBtn = root.querySelector('[data-pause]');
      pauseBtn?.addEventListener('click', () => {
        paused = !paused; const a = autoplay(); if (a) { paused ? a.stop() : a.play(); }
        pauseBtn.setAttribute('aria-pressed', String(paused));
        pauseBtn.setAttribute('aria-label', paused ? 'Reprendre le défilement' : 'Mettre le défilement en pause');
      });
      embla.on('autoplay:play', () => { if (paused) autoplay()?.stop(); });
      // Au toucher, l'autoplay ne repart qu'une fois le geste fini
      root.addEventListener('touchstart', () => autoplay()?.stop(), { passive: true });
      root.addEventListener('touchend', () => { if (!paused) setTimeout(() => autoplay()?.play(), 1200); }, { passive: true });

      root.querySelector('[data-prev]')?.addEventListener('click', () => nav(-1));
      root.querySelector('[data-next]')?.addEventListener('click', () => nav(1));
      root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { nav(-1); e.preventDefault(); }
        if (e.key === 'ArrowRight') { nav(1); e.preventDefault(); }
      });
      embla.on('pointerDown', () => track.classList.add('ca-is-dragging'));
      embla.on('pointerUp', () => setTimeout(() => track.classList.remove('ca-is-dragging'), 50));
      // Le viewport est focusable pour le clavier, sans voler le focus aux boutons
      if (!vp.hasAttribute('tabindex')) vp.setAttribute('tabindex', '0');
    });
  };

  /* ---------------------------------------------------------------- */
  /* 4. Header : discret en descendant, de retour en remontant            */
  /* ---------------------------------------------------------------- */
  const header = () => {
    const h = document.querySelector('.ca-header');
    if (!h || h._motion) return; h._motion = true;
    let last = scrollY, hidden = false, raf = 0;
    const hide = (v) => { if (v !== hidden) { hidden = v; h.classList.toggle('ca-header--hidden', v); } };
    const tick = () => {
      raf = 0;
      const y = Math.max(0, scrollY), dy = y - last;
      const top = parseFloat(getComputedStyle(html).getPropertyValue('--ca-top')) || 120;
      h.classList.toggle('ca-header--scrolled', y > 8);
      if (html.classList.contains('ca-menu-open') || h.matches(':focus-within')) { hide(false); last = y; return; }
      if (y <= top) hide(false);
      else if (dy > 8) hide(true);
      else if (dy < -8) hide(false);
      last = y;
    };
    addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(tick); }, { passive: true });
    h.addEventListener('focusin', () => hide(false));
    tick();
  };

  /* ---------------------------------------------------------------- */
  /* 5. Compteur du panier : petit sursaut quand casa.js le met à jour   */
  /* ---------------------------------------------------------------- */
  const bag = () => {
    document.querySelectorAll('[data-cart-count]').forEach((n) => {
      if (n._motion) return; n._motion = true;
      new MutationObserver(() => { n.classList.remove('ca-bump'); void n.offsetWidth; n.classList.add('ca-bump'); })
        .observe(n, { childList: true, characterData: true, subtree: true });
    });
  };

  /* ---------------------------------------------------------------- */
  /* 6. Lenis : défilement lissé à la molette (opt-in, jamais au toucher) */
  /* ---------------------------------------------------------------- */
  const smooth = () => {
    if (!cfg.smooth || !window.Lenis || reduce.matches || design || window.casaLenis) return;
    window.casaLenis = new window.Lenis({
      autoRaf: true, lerp: 0.1, anchors: { offset: -24 },
      prevent: (node) => !!(node.classList && (node.classList.contains('ca-drawer') || node.hasAttribute('data-lenis-prevent')))
    });
  };

  /* ---------------------------------------------------------------- */
  const init = (scope) => { autoTag(scope); sliders(scope); hero(scope); reveal(scope); };
  const boot = () => { init(document); header(); bag(); smooth(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
  // Éditeur de thème Shopify : une section rechargée se ré-initialise
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  reduce.addEventListener?.('change', () => { if (reduce.matches) revealNow([...document.querySelectorAll('[data-reveal], [data-hero]')]); });
})();
